/**
 * Avatar Component
 * Displays user avatar with optional status indicator
 */

import { ImgHTMLAttributes, useState } from 'react';
import { cn } from '@/utils/classNames';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export type StatusType = 'online' | 'away' | 'busy' | 'offline';

export interface AvatarProps
  extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'size'> {
  src?: string;
  alt: string;
  size?: AvatarSize;
  status?: StatusType;
}

const sizeStyles: Record<AvatarSize, string> = {
  xs: 'h-6 w-6',
  sm: 'h-8 w-8',
  md: 'h-10 w-10',
  lg: 'h-12 w-12',
  xl: 'h-16 w-16',
};

const statusStyles: Record<StatusType, string> = {
  online: 'bg-status-online',
  away: 'bg-status-away',
  busy: 'bg-status-busy',
  offline: 'bg-status-offline',
};

const statusSizes: Record<AvatarSize, string> = {
  xs: 'h-1.5 w-1.5',
  sm: 'h-2 w-2',
  md: 'h-3 w-3',
  lg: 'h-3.5 w-3.5',
  xl: 'h-4 w-4',
};

export function Avatar({
  src,
  alt,
  size = 'md',
  status,
  className,
  ...props
}: AvatarProps) {
  const [imageError, setImageError] = useState(false);

  // Generate initials from alt text
  const initials = alt
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className={cn('relative inline-block', className)}>
      {src && !imageError ? (
        <img
          src={src}
          alt={alt}
          className={cn(
            'avatar object-cover',
            'border-2 border-bg-primary',
            'rounded-full',
            sizeStyles[size]
          )}
          onError={() => setImageError(true)}
          {...props}
        />
      ) : (
        <div
          className={cn(
            'avatar flex items-center justify-center',
            'bg-stellar-primary-light text-white',
            'font-semibold rounded-full',
            'border-2 border-bg-primary',
            sizeStyles[size]
          )}
        >
          <span
            className={cn(
              size === 'xs' && 'text-[8px]',
              size === 'sm' && 'text-xs',
              size === 'md' && 'text-sm',
              size === 'lg' && 'text-base',
              size === 'xl' && 'text-lg'
            )}
          >
            {initials}
          </span>
        </div>
      )}

      {status && (
        <span
          className={cn(
            'status-dot absolute bottom-0 right-0',
            'rounded-full border-2 border-bg-primary',
            statusStyles[status],
            statusSizes[size]
          )}
          aria-label={`Status: ${status}`}
        />
      )}
    </div>
  );
}
